import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, QrCode, Keyboard, Fingerprint, X, Camera, Shield } from 'lucide-react';
import axios from 'axios';
import { MFAVerifyDialog } from '@/components/MFAVerifyDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loginMethod, setLoginMethod] = useState('credentials'); // 'credentials', 'qr', 'biometric'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [qrScanning, setQrScanning] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [savedUsername, setSavedUsername] = useState('');
  
  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaUsername, setMfaUsername] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [pendingCredentials, setPendingCredentials] = useState(null);
  
  // MFA Setup State (for haendler/instandhaltung first-time login)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
  const [mfaSetupBackupCodes, setMfaSetupBackupCodes] = useState(null);
  const [mfaSetupTempToken, setMfaSetupTempToken] = useState(null);
  const [mfaSetupUserId, setMfaSetupUserId] = useState(null);
  
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Check for biometric availability and saved session
  useEffect(() => {
    // Check if Web Authentication API is available (for biometrics)
    if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setBiometricAvailable(available))
        .catch(() => setBiometricAvailable(false));
    }

    // Check for saved session
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('savedUsername');
    if (savedToken && savedUser) {
      setHasSavedSession(true);
      setSavedUsername(savedUser);
    }
  }, []);

  // Cleanup QR scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(username, password, rememberMe);
        
        // Check if MFA is required
        if (result.mfa_required) {
          setMfaRequired(true);
          setMfaUserId(result.user_id);
          setMfaUsername(result.username);
          setPendingCredentials({ username, password, rememberMe });
          setLoading(false);
          return;
        }
        
        // Check if MFA setup is required (haendler/instandhaltung first login)
        if (result.mfa_setup_required) {
          setMfaSetupRequired(true);
          setMfaSetupUserId(result.user_id);
          setMfaSetupTempToken(result.temp_token);
          setPendingCredentials({ username, password, rememberMe });
          // Start MFA setup
          try {
            const setupRes = await axios.post(
              `${API_URL}/users/${result.user_id}/mfa/setup`,
              {},
              { headers: { Authorization: `Bearer ${result.temp_token}` } }
            );
            setMfaSetupData(setupRes.data);
          } catch (setupErr) {
            toast.error('MFA-Setup konnte nicht gestartet werden.');
            setMfaSetupRequired(false);
          }
          setLoading(false);
          return;
        }
        
        // Save username if remember me is checked
        if (rememberMe) {
          localStorage.setItem('savedUsername', username);
        }
        
        // Show MFA warning if present
        if (result.mfa_warning) {
          toast.warning(result.mfa_warning, { duration: 8000 });
        }
        
        // No toast needed - navigation to homepage shows login success
        navigate('/');
      } else {
        const response = await register(username, password, firstName, lastName, email);
        if (response.pending) {
          toast.info(response.message, { duration: 6000 });
        } else {
          const roleLabels = {
            admin: 'Administrator',
            manager: 'Betriebsleitung',
            wiegemeister: 'Wiegemeister',
            worker: 'Mitarbeiter',
            haendler: 'Händler',
            instandhaltung: 'Instandhaltung'
          };
          toast.success(`Willkommen! Sie sind als ${roleLabels[response.role] || 'Mitarbeiter'} registriert.`);
          navigate('/');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Anmeldung');
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification
  const handleMFAVerify = async (mfaCode, trustDevice) => {
    setLoading(true);
    setMfaError(null);
    
    try {
      const result = await login(
        pendingCredentials.username,
        pendingCredentials.password,
        pendingCredentials.rememberMe,
        mfaCode,
        trustDevice
      );
      
      // Save username if remember me was checked
      if (pendingCredentials.rememberMe) {
        localStorage.setItem('savedUsername', pendingCredentials.username);
      }
      
      // Show MFA warning if present
      if (result.mfa_warning) {
        toast.warning(result.mfa_warning, { duration: 8000 });
      }
      
      setMfaRequired(false);
      setPendingCredentials(null);
      navigate('/');
    } catch (error) {
      setMfaError(error.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaUserId(null);
    setMfaUsername('');
    setMfaError(null);
    setPendingCredentials(null);
  };

  const startQRScanner = async () => {
    setQrScanning(true);
    setLoginMethod('qr');

    // Dynamic import of html5-qrcode
    const { Html5Qrcode } = await import('html5-qrcode');
    
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 }
          },
          async (decodedText) => {
            // QR code successfully scanned
            await html5QrCode.stop();
            setQrScanning(false);
            handleQRLogin(decodedText);
          },
          (errorMessage) => {
            // QR code scanning error (this is normal during scanning)
          }
        );
      } catch (err) {
        toast.error('Kamera konnte nicht gestartet werden. Bitte Berechtigung erteilen.');
        setQrScanning(false);
        setLoginMethod('credentials');
      }
    }, 100);
  };

  const stopQRScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {}
      // Non-critical: silently handled
    }
    setQrScanning(false);
    setLoginMethod('credentials');
  };

  const handleQRLogin = async (qrToken) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/qr-login`, {
        qr_token: qrToken
      });

      const { token, csrf_token, user } = response.data;
      
      // Store tokens
      localStorage.setItem('token', token);
      sessionStorage.setItem('token', token);
      localStorage.setItem('savedUsername', user.username);
      
      // Set axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (csrf_token) {
        axios.defaults.headers.common['X-CSRF-Token'] = csrf_token;
      }

      toast.success(`Willkommen zurück, ${user.first_name || user.username}!`);
      
      // Reload to update auth context
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'QR-Code Login fehlgeschlagen');
      setLoginMethod('credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      // Check if we have a saved token
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        toast.error('Keine gespeicherte Sitzung gefunden. Bitte melden Sie sich zuerst normal an.');
        setLoginMethod('credentials');
        setLoading(false);
        return;
      }

      // Request biometric authentication
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32),
          timeout: 60000,
          userVerification: "required",
          rpId: window.location.hostname,
        }
      }).catch(() => null);

      if (!credential) {
        // Fallback: use simple confirmation on mobile
        const confirmed = window.confirm('Identität bestätigen?');
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      // Refresh the token
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      const response = await axios.post(`${API_URL}/auth/refresh-token`);
      
      const { token, csrf_token, user } = response.data;
      
      // Update stored tokens
      localStorage.setItem('token', token);
      sessionStorage.setItem('token', token);
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (csrf_token) {
        axios.defaults.headers.common['X-CSRF-Token'] = csrf_token;
      }

      // No toast needed - navigation shows login success
      window.location.href = '/';
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
        localStorage.removeItem('token');
        setHasSavedSession(false);
      } else {
        toast.error('Biometrische Anmeldung fehlgeschlagen');
      }
      setLoginMethod('credentials');
    } finally {
      setLoading(false);
    }
  };

  const clearSavedSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('savedUsername');
    sessionStorage.removeItem('token');
    setHasSavedSession(false);
    setSavedUsername('');
    // No toast needed - UI update shows session cleared
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-border/50">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-20 h-20 flex items-center justify-center mb-2">
            <img src="/logo-150.png" alt="MV Logo" className="w-full h-full object-contain" />
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight">
            MV | LagerManager
          </CardTitle>
          <CardDescription className="text-base">
            {isLogin ? 'Melden Sie sich an' : 'Erstellen Sie Ihr Konto'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Quick Login Options (only show on login, not register) */}
          {isLogin && loginMethod === 'credentials' && (
            <div className="mb-6 space-y-3">
              {/* Biometric login button (Option 3) */}
              {hasSavedSession && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Schnell-Anmeldung</span>
                    <button 
                      onClick={clearSavedSession}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Vergessen
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-14 text-base"
                    onClick={handleBiometricLogin}
                    disabled={loading}
                  >
                    <Fingerprint className="mr-3 h-6 w-6" />
                    Als {savedUsername} anmelden
                  </Button>
                </div>
              )}
              
              {/* QR Code Scanner Button (Option 2) */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-12"
                onClick={startQRScanner}
                disabled={loading}
              >
                <QrCode className="mr-2 h-5 w-5" />
                Mit QR-Code anmelden
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    oder mit Zugangsdaten
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* QR Scanner View */}
          {loginMethod === 'qr' && qrScanning && (
            <div className="space-y-4">
              <div className="relative">
                <div id="qr-reader" ref={scannerRef} className="w-full rounded-lg overflow-hidden" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-background/80 hover:bg-background"
                  onClick={stopQRScanner}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Camera className="h-4 w-4" />
                  Halten Sie den QR-Code vor die Kamera
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={stopQRScanner}
              >
                <Keyboard className="mr-2 h-4 w-4" />
                Zurück zur normalen Anmeldung
              </Button>
            </div>
          )}

          {/* Standard Login Form */}
          {loginMethod === 'credentials' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-sm font-medium">
                        Vorname *
                      </Label>
                      <Input
                        id="first_name"
                        type="text"
                        placeholder="Max"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-sm font-medium">
                        Nachname *
                      </Label>
                      <Input
                        id="last_name"
                        type="text"
                        placeholder="Mustermann"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="max.mustermann@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium">
                  Benutzername {!isLogin && '*'}
                </Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  type="text"
                  placeholder="Ihr Benutzername"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Passwort {!isLogin && '*'}
                </Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="Ihr Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12"
                />
              </div>
              
              {/* Remember Me Checkbox (Option 3) */}
              {isLogin && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                  />
                  <Label 
                    htmlFor="remember-me" 
                    className="text-sm font-normal cursor-pointer"
                  >
                    Angemeldet bleiben (30 Tage)
                  </Label>
                </div>
              )}
              
              <Button
                type="submit"
                data-testid="login-submit-button"
                className="w-full h-12 text-base font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Wird geladen...
                  </>
                ) : isLogin ? (
                  'Anmelden'
                ) : (
                  'Registrieren'
                )}
              </Button>
            </form>
          )}
          
          {loginMethod === 'credentials' && (
            <>
              <div className="mt-6 text-center">
                <button
                  type="button"
                  data-testid="toggle-auth-mode-button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-primary hover:underline"
                >
                  {isLogin
                    ? 'Noch kein Konto? Jetzt registrieren'
                    : 'Bereits ein Konto? Anmelden'}
                </button>
              </div>
              {!isLogin && (
                <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-md">
                  <p className="text-xs text-muted-foreground">
                    💡 <strong>Hinweis:</strong> Der erste registrierte Benutzer wird automatisch als Administrator eingerichtet.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* MFA Verification Dialog */}
      <MFAVerifyDialog
        open={mfaRequired}
        onOpenChange={(open) => !open && handleMFACancel()}
        onVerify={handleMFAVerify}
        loading={loading}
        error={mfaError}
        username={mfaUsername}
      />
      
      {/* MFA Forced Setup Dialog (for haendler/instandhaltung) */}
      {mfaSetupRequired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md" data-testid="mfa-forced-setup-dialog">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                MFA-Einrichtung erforderlich
              </CardTitle>
              <CardDescription>
                Für Ihre Rolle ist die Zwei-Faktor-Authentifizierung Pflicht. Bitte scannen Sie den QR-Code mit einer Authenticator-App.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mfaSetupBackupCodes ? (
                /* Show backup codes after successful setup */
                <div className="space-y-4">
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">MFA erfolgreich eingerichtet!</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Backup-Codes (sicher aufbewahren!):</p>
                    <div className="grid grid-cols-2 gap-1 p-3 bg-muted rounded-lg font-mono text-sm" data-testid="mfa-backup-codes">
                      {mfaSetupBackupCodes.map((code, i) => (
                        <span key={i}>{code}</span>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Jeder Code kann nur einmal verwendet werden.</p>
                  </div>
                  <Button
                    className="w-full"
                    data-testid="mfa-setup-continue-btn"
                    onClick={async () => {
                      try {
                        const result = await login(
                          pendingCredentials.username,
                          pendingCredentials.password,
                          pendingCredentials.rememberMe
                        );
                        if (result.mfa_required) {
                          setMfaSetupRequired(false);
                          setMfaSetupBackupCodes(null);
                          setMfaRequired(true);
                          setMfaUserId(result.user_id);
                          setMfaUsername(result.username);
                        } else {
                          navigate('/');
                        }
                      } catch {
                        toast.error('Login fehlgeschlagen. Bitte erneut anmelden.');
                        setMfaSetupRequired(false);
                        setMfaSetupBackupCodes(null);
                      }
                    }}
                  >
                    Weiter zur Anmeldung
                  </Button>
                </div>
              ) : mfaSetupData ? (
                /* Show QR Code and verification input */
                <div className="space-y-4">
                  {mfaSetupData.qr_code && (
                    <div className="flex justify-center">
                      <img
                        src={mfaSetupData.qr_code}
                        alt="MFA QR Code"
                        className="w-48 h-48 rounded-lg border"
                        data-testid="mfa-qr-code"
                      />
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Manueller Code:</p>
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded select-all">{mfaSetupData.secret}</code>
                  </div>
                  <div className="space-y-2">
                    <Label>Bestätigungscode eingeben</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={mfaSetupCode}
                      onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-2xl tracking-widest font-mono"
                      data-testid="mfa-setup-code-input"
                      autoFocus
                    />
                  </div>
                  <Button
                    className="w-full"
                    disabled={mfaSetupCode.length !== 6 || mfaSetupLoading}
                    data-testid="mfa-setup-verify-btn"
                    onClick={async () => {
                      setMfaSetupLoading(true);
                      try {
                        const res = await axios.post(
                          `${API_URL}/users/${mfaSetupUserId}/mfa/verify`,
                          { code: mfaSetupCode },
                          { headers: { Authorization: `Bearer ${mfaSetupTempToken}` } }
                        );
                        setMfaSetupBackupCodes(res.data.backup_codes);
                        toast.success('MFA erfolgreich eingerichtet!');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Ungültiger Code. Bitte erneut versuchen.');
                        setMfaSetupCode('');
                      } finally {
                        setMfaSetupLoading(false);
                      }
                    }}
                  >
                    {mfaSetupLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird überprüft...</>
                    ) : (
                      'Code bestätigen'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
